import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  updateLessonProgress,
  getLessonProgressForEnrollment,
  type ElearningEnrollment 
} from '../../services/elearningOrderService';
import { getStudentProfileByToken, getStudentEnrollmentDetails } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { 
  Play, 
  Pause,
  SkipBack,
  SkipForward,
  BookOpen,
  CheckCircle,
  Clock,
  FileText,
  Video,
  Headphones,
  Download,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Award,
  Star,
  MessageCircle,
  Share2,
  Volume2,
  VolumeX,
  Maximize,
  Settings,
  List,
  User,
  HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import QuizPlayer from '../../components/student/quiz/QuizPlayer';

const CoursePlayerPage: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);

  const [enrollment, setEnrollment] = useState<any>(null);
  const [currentModule, setCurrentModule] = useState<any>(null);
  const [currentLesson, setCurrentLesson] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lessonProgress, setLessonProgress] = useState<Record<string, number>>({});
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [moduleProgress, setModuleProgress] = useState<Record<string, number>>({});
  const [isQuizPlayerOpen, setIsQuizPlayerOpen] = useState(false);
  const [currentQuizLessonId, setCurrentQuizLessonId] = useState<string | null>(null);

  useEffect(() => {
    if (!enrollmentId || !tenant?.id) {
      console.log('useEffect - enrollmentId ou tenant.id manquant, impossible de charger les détails');
      return;
    }
    
    console.log('useEffect - Démarrage de la vérification d\'authentification');
    const checkAuthentication = async () => {
      const token = localStorage.getItem('elearning_student_token');
      console.log('useEffect - Token trouvé:', token ? 'Oui' : 'Non');
      
      if (token) {
        try {
          console.log('useEffect - Vérification du profil étudiant avec le token');
          const profile = await getStudentProfileByToken(token);
          if (profile) {
            console.log('useEffect - Profil étudiant valide:', profile.id);
            // Utiliser le callback de setState pour s'assurer que l'état est mis à jour avant d'appeler loadEnrollmentDetails
            setStudentProfile(profile);
            
            // Stocker le profil dans une variable locale pour l'utiliser directement
            const currentProfile = profile;
            
            // Délai plus long pour s'assurer que tous les états sont mis à jour
            setTimeout(() => {
              console.log('useEffect - Appel de loadEnrollmentDetails avec profil:', currentProfile.id);
              // Appeler loadEnrollmentDetails avec le profil en paramètre pour éviter de dépendre de l'état React
              loadEnrollmentDetails(currentProfile);
            }, 300);
          } else {
            console.log('useEffect - Profil étudiant non trouvé, redirection vers l\'authentification');
            // Token invalide ou expiré
            localStorage.removeItem('elearning_student_token');
            navigate('/elearning/auth', { state: { redirectTo: `/elearning/player/${enrollmentId}` } });
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          localStorage.removeItem('elearning_student_token');
          navigate('/elearning/auth', { state: { redirectTo: `/elearning/player/${enrollmentId}` } });
        }
      } else {
        console.log('useEffect - Aucun token trouvé, redirection vers l\'authentification');
        navigate('/elearning/auth', { state: { redirectTo: `/elearning/player/${enrollmentId}` } });
      }
    };
    
    checkAuthentication();
  }, [enrollmentId, tenant?.id, navigate]);

  const loadEnrollmentDetails = async (profile?: any) => {
    // Utiliser le profil passé en paramètre ou l'état studentProfile
    const studentId = profile?.id || studentProfile?.id;
    
    if (!enrollmentId || !tenant?.id || !studentId) {
      console.log('loadEnrollmentDetails - Paramètres manquants:', { enrollmentId, tenantId: tenant?.id, studentId });
      return;
    }

    console.log('loadEnrollmentDetails - Début du chargement des données', { enrollmentId, tenantId: tenant?.id, studentId });
    setIsLoading(true);
    try {
      console.log('loadEnrollmentDetails - Appel de getStudentEnrollmentDetails avec studentId:', studentId);
      const enrollmentData = await getStudentEnrollmentDetails(studentId, tenant.id, enrollmentId);
      console.log('loadEnrollmentDetails - Réponse de getStudentEnrollmentDetails:', enrollmentData);
      
      // Vérifier si les données sont complètes
      if (!enrollmentData) {
        console.error('loadEnrollmentDetails - Aucune donnée d\'inscription reçue');
        throw new Error('Aucune donnée d\'inscription reçue');
      }
      
      console.log('loadEnrollmentDetails - Données du cours:', enrollmentData.course);
      console.log('loadEnrollmentDetails - Modules:', enrollmentData.modules);
      
      // S'assurer que les modules sont bien définis
      if (!enrollmentData.modules || enrollmentData.modules.length === 0) {
        console.error('loadEnrollmentDetails - Aucun module trouvé dans les données');
        throw new Error('Aucun module trouvé pour ce cours');
      }
      
      // Mettre à jour l'état avec les données reçues
      setEnrollment(enrollmentData);
      
      // Sélectionner le premier module
      const firstModule = enrollmentData.modules[0];
      console.log('loadEnrollmentDetails - Sélection du premier module:', firstModule);
      
      // Vérifier que le module a des leçons
      if (!firstModule.lessons || firstModule.lessons.length === 0) {
        console.error('loadEnrollmentDetails - Le premier module n\'a pas de leçons');
        throw new Error('Aucune leçon trouvée dans ce module');
      }
      
      // Initialiser tous les modules comme rétractés par défaut
      const modulesState: Record<string, boolean> = {};
      enrollmentData.modules.forEach((module: any) => {
        modulesState[module.id] = false;
      });
      // Développer uniquement le module actuel
      if (firstModule.id) {
        modulesState[firstModule.id] = true;
      }
      setExpandedModules(modulesState);
      
      // Récupérer la progression des leçons
      console.log('loadEnrollmentDetails - Récupération de la progression des leçons');
      try {
        const lessonProgressData = await getLessonProgressForEnrollment(enrollmentId);
        console.log('loadEnrollmentDetails - Progression des leçons reçue:', lessonProgressData);
        setLessonProgress(lessonProgressData);
        
        // Calculer la progression par module
        const moduleProgressData: Record<string, number> = {};
        enrollmentData.modules.forEach((module: any) => {
          if (module.lessons && module.lessons.length > 0) {
            let completedLessons = 0;
            module.lessons.forEach((lesson: any) => {
              if (lessonProgressData[lesson.id] === 100) {
                completedLessons++;
              }
            });
            moduleProgressData[module.id] = Math.round((completedLessons / module.lessons.length) * 100);
          } else {
            moduleProgressData[module.id] = 0;
          }
        });
        setModuleProgress(moduleProgressData);
      } catch (progressError) {
        console.error('loadEnrollmentDetails - Erreur lors de la récupération de la progression des leçons:', progressError);
        // Continuer même en cas d'erreur
      }
      
      // Définir le module et la leçon actuels
      setCurrentModule(firstModule);
      const firstLesson = firstModule.lessons[0];
      console.log('loadEnrollmentDetails - Sélection de la première leçon:', firstLesson);
      setCurrentLesson(firstLesson);
    } catch (error) {
      console.error('loadEnrollmentDetails - Erreur lors du chargement des détails:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le cours',
        variant: 'destructive'
      });
      navigate('/elearning/student/dashboard');
    } finally {
      // S'assurer que isLoading est mis à false après un court délai
      // pour permettre aux états de se mettre à jour
      console.log('loadEnrollmentDetails - Fin du chargement, isLoading sera mis à false');
      setTimeout(() => {
        console.log('loadEnrollmentDetails - Mise à jour de isLoading à false');
        setIsLoading(false);
      }, 200);
    }
  };

  // Mettre à jour la progression de la leçon
  const updateProgress = async (lessonId: string, progress: number) => {
    if (!enrollmentId) return;
    
    try {
      await updateLessonProgress(enrollmentId, lessonId, progress);
      setLessonProgress((prevProgress) => ({
        ...prevProgress,
        [lessonId]: progress
      }));
      
      // Mettre à jour la progression du module contenant cette leçon
      if (currentModule?.id && currentModule.lessons) {
        const moduleId = currentModule.id;
        const lessons = currentModule.lessons;
        let completedLessons = 0;
        
        // Récupérer l'état actuel de la progression des leçons
        const currentLessonProgress = { ...lessonProgress, [lessonId]: progress };
        
        lessons.forEach((lesson: any) => {
          const lessonProgressValue = currentLessonProgress[lesson.id] || 0;
          if (lessonProgressValue === 100) {
            completedLessons++;
          }
        });
        
        const moduleProgressValue = Math.round((completedLessons / lessons.length) * 100);
        setModuleProgress(prev => ({
          ...prev,
          [moduleId]: moduleProgressValue
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la progression:', error);
    }
  };

  const markLessonAsCompleted = async (lessonId: string) => {
    if (!enrollmentId || !studentProfile?.id) return;

    try {
      console.log('markLessonAsCompleted - Mise à jour de la progression', { enrollmentId, lessonId });
      
      await updateProgress(lessonId, 100);
      
      // Récupérer les détails mis à jour de l'inscription pour avoir la progression globale
      console.log('markLessonAsCompleted - Récupération des détails mis à jour');
      if (tenant?.id && enrollmentId) {
        const updatedEnrollmentDetails = await getStudentEnrollmentDetails(studentProfile.id, tenant.id, enrollmentId);
        
        if (updatedEnrollmentDetails) {
          console.log('markLessonAsCompleted - Progression mise à jour:', updatedEnrollmentDetails.progress_percentage);
          
          // Mettre à jour l'état de l'inscription avec la nouvelle progression
          setEnrollment((prev: any) => ({
            ...prev,
            progress_percentage: updatedEnrollmentDetails.progress_percentage
          }));
        }
      }
      
      toast({
        title: 'Leçon terminée !',
        description: 'Votre progression a été sauvegardée',
        variant: 'default'
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la progression',
        variant: 'destructive'
      });
    }
  };

  const openQuizPlayer = (lessonId: string) => {
    setCurrentQuizLessonId(lessonId);
    setIsQuizPlayerOpen(true);
  };

  const closeQuizPlayer = () => {
    setIsQuizPlayerOpen(false);
    setCurrentQuizLessonId(null);
  };

  const handleQuizComplete = async (attempt: any) => {
    if (!currentQuizLessonId) return;

    try {
      // Marquer la leçon comme terminée si le quiz est réussi
      if (attempt.passed) {
        await updateProgress(currentQuizLessonId, 100);
        
        // Mettre à jour la progression globale
        if (studentProfile?.id && tenant?.id && enrollmentId) {
          const updatedEnrollmentDetails = await getStudentEnrollmentDetails(studentProfile.id, tenant.id, enrollmentId);
          if (updatedEnrollmentDetails) {
            setEnrollment((prev: any) => ({
              ...prev,
              progress_percentage: updatedEnrollmentDetails.progress_percentage
            }));
          }
        }
        
        toast({
          title: 'Quiz réussi !',
          description: `Score: ${Math.round(attempt.percentage)}% - Leçon terminée`,
          variant: 'default'
        });
      } else {
        toast({
          title: 'Quiz terminé',
          description: `Score: ${Math.round(attempt.percentage)}% - Vous pouvez réessayer`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la progression du quiz:', error);
    }
  };

  const navigateToLesson = (moduleId: string, lessonId: string) => {
    const module = enrollment?.course?.modules?.find((m: any) => m.id === moduleId);
    const lesson = module?.lessons?.find((l: any) => l.id === lessonId);
    
    if (module && lesson) {
      setCurrentModule(module);
      setCurrentLesson(lesson);
      
      // Développer le module sélectionné
      setExpandedModules(prev => ({
        ...prev,
        [moduleId]: true
      }));
    }
  };
  
  // Fonction pour basculer l'état d'expansion d'un module
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getNextLesson = () => {
    if (!currentModule || !currentLesson || !enrollment?.course?.modules) return null;

    const currentModuleIndex = enrollment.course.modules.findIndex((m: any) => m.id === currentModule.id);
    const currentLessonIndex = currentModule.lessons.findIndex((l: any) => l.id === currentLesson.id);

    // Leçon suivante dans le même module
    if (currentLessonIndex < currentModule.lessons.length - 1) {
      return {
        module: currentModule,
        lesson: currentModule.lessons[currentLessonIndex + 1]
      };
    }

    // Premier leçon du module suivant
    if (currentModuleIndex < enrollment.course.modules.length - 1) {
      const nextModule = enrollment.course.modules[currentModuleIndex + 1];
      if (nextModule.lessons?.length > 0) {
        return {
          module: nextModule,
          lesson: nextModule.lessons[0]
        };
      }
    }

    return null;
  };

  const getPreviousLesson = () => {
    if (!currentModule || !currentLesson || !enrollment?.course?.modules) return null;

    const currentModuleIndex = enrollment.course.modules.findIndex((m: any) => m.id === currentModule.id);
    const currentLessonIndex = currentModule.lessons.findIndex((l: any) => l.id === currentLesson.id);

    // Leçon précédente dans le même module
    if (currentLessonIndex > 0) {
      return {
        module: currentModule,
        lesson: currentModule.lessons[currentLessonIndex - 1]
      };
    }

    // Dernière leçon du module précédent
    if (currentModuleIndex > 0) {
      const prevModule = enrollment.course.modules[currentModuleIndex - 1];
      if (prevModule.lessons?.length > 0) {
        return {
          module: prevModule,
          lesson: prevModule.lessons[prevModule.lessons.length - 1]
        };
      }
    }

    return null;
  };

  const goToNextLesson = () => {
    const next = getNextLesson();
    if (next) {
      setCurrentModule(next.module);
      setCurrentLesson(next.lesson);
    }
  };

  const goToPreviousLesson = () => {
    const prev = getPreviousLesson();
    if (prev) {
      setCurrentModule(prev.module);
      setCurrentLesson(prev.lesson);
    }
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video size={16} />;
      case 'audio': return <Headphones size={16} />;
      case 'text': return <FileText size={16} />;
      case 'quiz': return <CheckCircle size={16} />;
      default: return <BookOpen size={16} />;
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  console.log('CoursePlayerPage - État actuel:', {
    isLoading,
    enrollment: enrollment ? 'Présent' : 'Absent',
    modules: enrollment?.modules ? `${enrollment.modules.length} modules` : 'Aucun module',
    currentModule: currentModule ? currentModule.title : 'Aucun module sélectionné',
    currentLesson: currentLesson ? currentLesson.title : 'Aucune leçon sélectionnée'
  });

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="bg-[#111216] text-white min-h-screen">
          <div className="flex flex-col justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
            <span className="mt-6 text-xl text-white/80">Chargement du cours...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!enrollment) {
    return (
      <PublicLayout>
        <div className="bg-[#111216] text-white min-h-screen">
          <div className="container mx-auto py-12">
            <div className="text-center py-20">
              <BookOpen className="h-16 w-16 text-white/30 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-4 text-white">Cours non trouvé</h2>
              <p className="mb-6 text-white/80">Ce cours n'existe pas ou vous n'y avez pas accès.</p>
              <Button 
                onClick={() => navigate('/elearning/student/dashboard')}
                className="bg-red-600 hover:bg-red-700"
              >
                Retour au tableau de bord
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOMetaTags 
        title={`${enrollment.course?.title} - Lecteur de cours`}
        description={`Suivez le cours ${enrollment.course?.title} et progressez dans votre apprentissage.`}
        keywords="cours, apprentissage, e-learning, formation"
      />
      
      <div className="text-white relative overflow-hidden">
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant && tenant.config?.bg ? `url(${tenant.config.bg})` : 'url(/default-bg.jpg)',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
        {/* Header du lecteur */}
        <div className="bg-black/40 border-b border-white/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/elearning/student/dashboard')}
                className="text-white hover:bg-white/10"
              >
                <ChevronLeft size={16} className="mr-1" />
                Retour
              </Button>
              
              <Separator orientation="vertical" className="h-6 bg-white/20" />
              
              <div>
                <h1 className="font-semibold text-lg line-clamp-1">
                  {enrollment.course?.title}
                </h1>
                <p className="text-sm text-white/60">
                  {currentModule?.title} • {currentLesson?.title}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="text-white hover:bg-white/10"
              >
                {isSidebarOpen ? <X size={16} /> : <Menu size={16} />}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex h-[calc(100vh-80px)]">
          {/* Contenu principal */}
          <div className={`flex-1 transition-all duration-300 ${isSidebarOpen ? 'mr-96' : ''}`}>
            <div className="h-full flex flex-col">
              {/* Zone de contenu */}
              <div className="flex-1 bg-black/20 flex items-center justify-center">
                {currentLesson ? (
                  <div className="w-full max-w-4xl mx-auto p-6">
                    {/* Contenu de la leçon */}
                    <div className="bg-black/40 rounded-lg p-8 mb-6">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          {getContentIcon(currentLesson.content_type)}
                          <div>
                            <h2 className="text-2xl font-bold">{currentLesson.title}</h2>
                            <p className="text-white/60">{currentLesson.description}</p>
                          </div>
                        </div>
                        
                        {currentLesson.duration_seconds && (
                          <Badge variant="outline" className="border-white/20 text-white">
                            <Clock size={12} className="mr-1" />
                            {formatDuration(currentLesson.duration_seconds)}
                          </Badge>
                        )}
                      </div>

                      {/* Simulation du contenu */}
                      {currentLesson.content_type === 'video' && (
                        <div className="aspect-video bg-black/60 rounded-lg flex items-center justify-center mb-6">
                          <div className="text-center">
                            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                              {isPlaying ? <Pause size={32} /> : <Play size={32} />}
                            </div>
                            <p className="text-white/60">Lecteur vidéo (simulation)</p>
                          </div>
                        </div>
                      )}

                      {currentLesson.content_type === 'text' && (
                        <div className="prose prose-invert max-w-none mb-6">
                          <p className="text-white/80 leading-relaxed">
                            {currentLesson.content || 
                            `Ceci est le contenu textuel de la leçon "${currentLesson.title}". 
                            Dans une implémentation réelle, ce contenu serait récupéré depuis la base de données 
                            et pourrait inclure du texte formaté, des images, des liens, etc.`}
                          </p>
                        </div>
                      )}

                      {currentLesson.content_type === 'quiz' && (
                        <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg p-6 mb-6">
                          <div className="flex items-center mb-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg flex items-center justify-center mr-4">
                              <HelpCircle className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white mb-1">Quiz de validation</h3>
                              <p className="text-purple-300 text-sm">Testez vos connaissances acquises dans cette leçon</p>
                            </div>
                          </div>
                          <p className="text-white/70 mb-6">
                            Ce quiz vous permettra de valider votre compréhension des concepts abordés dans cette leçon. 
                            Vous devez réussir le quiz pour progresser dans le cours.
                          </p>
                          <div className="flex items-center space-x-4">
                            <Button 
                              onClick={() => openQuizPlayer(currentLesson.id)}
                              className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white px-6 py-3"
                            >
                              <HelpCircle className="w-4 h-4 mr-2" />
                              Commencer le quiz
                            </Button>
                            <div className="text-sm text-white/60">
                              <div className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                <span>Progression sauvegardée automatiquement</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions de la leçon */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <Button
                            onClick={() => markLessonAsCompleted(currentLesson.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle size={16} className="mr-2" />
                            Marquer comme terminé
                          </Button>
                          
                          {currentLesson.resources_url && (
                            <Button
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/10"
                              onClick={() => window.open(currentLesson.resources_url, '_blank')}
                            >
                              <Download size={16} className="mr-2" />
                              Ressources
                            </Button>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToPreviousLesson}
                            disabled={!getPreviousLesson()}
                            className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
                          >
                            <SkipBack size={16} />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToNextLesson}
                            disabled={!getNextLesson()}
                            className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
                          >
                            <SkipForward size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <BookOpen size={64} className="mx-auto text-white/30 mb-6" />
                    <h3 className="text-xl font-bold mb-2">Aucune leçon sélectionnée</h3>
                    <p className="text-white/60">Sélectionnez une leçon dans la barre latérale pour commencer.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Barre latérale */}
          <AnimatePresence>
            {isSidebarOpen && (
              <motion.div
                initial={{ x: 384 }}
                animate={{ x: 0 }}
                exit={{ x: 384 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="fixed right-0 top-[80px] bottom-0 w-96 bg-gradient-to-br from-black/60 to-black/80 border-l border-white/10 backdrop-blur-md shadow-xl"
              >
                <div className="h-full flex flex-col">
                  {/* Header de la barre latérale */}
                  <div className="p-5 border-b border-white/10 bg-gradient-to-r from-black/40 to-black/20">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-lg tracking-tight">Contenu du cours</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsSidebarOpen(false)}
                        className="text-white hover:bg-white/10 rounded-full h-8 w-8 p-0 flex items-center justify-center"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    
                    {/* Progression globale */}
                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/70 font-medium">Progression du cours</span>
                        <span className="font-semibold text-white">{enrollment.progress_percentage}%</span>
                      </div>
                      <Progress value={enrollment.progress_percentage} className="h-2.5 bg-white/10" />
                    </div>
                  </div>

                  {/* Liste des modules et leçons */}
                  <ScrollArea className="flex-1 px-5 py-4">
                    <div className="space-y-5">
                      {enrollment.modules?.map((module: any, moduleIndex: number) => (
                        <div key={module.id} className="space-y-2 bg-black/20 rounded-xl overflow-hidden border border-white/5">
                          <button 
                            onClick={() => toggleModule(module.id)}
                            className="w-full flex items-start justify-between p-3.5 hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-start space-x-3 flex-1">
                              <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${expandedModules[module.id] ? 'bg-red-600' : 'bg-red-600/50'} transition-colors mt-0.5`}>
                                {expandedModules[module.id] ? 
                                  <ChevronDown size={16} className="text-white" /> : 
                                  <ChevronRight size={16} className="text-white" />
                                }
                              </div>
                              <div className="flex flex-col items-start w-full">
                                <div className="w-full flex items-center justify-between mb-1.5">
                                  <div className="flex items-center">
                                    <span className="text-[14px] text-white/80 font-medium bg-red-600/15 px-2 py-0.5 rounded-md">Module {moduleIndex + 1}</span>
                                    <span className="text-white/50 mx-1 text-sm">:</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Badge variant="outline" className="border-white/10 text-white/80 text-[10px] bg-yellow-600/15 px-2 py-0.5 font-medium tracking-wide rounded-md">
                                      {module.lessons?.length || 0} leçons
                                    </Badge>
                                    <span className="text-[10px] text-white/80 font-medium bg-green-600/15 px-2 py-0.5 rounded-md">
                                      {moduleProgress[module.id] || 0}% terminé
                                    </span>
                                  </div>
                                </div>
                                <h4 className="font-medium text-left w-full">
                                  <span className="text-white text-lg block leading-tight">{module.title}</span>
                                </h4>
                              </div>
                            </div>
                          </button>
                          
                          {expandedModules[module.id] && (
                            <div className="space-y-0.5 px-3 pb-3">
                              {module.lessons?.map((lesson: any, lessonIndex: number) => (
                                <button
                                  key={lesson.id}
                                  onClick={() => navigateToLesson(module.id, lesson.id)}
                                  className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                                    currentLesson?.id === lesson.id
                                      ? 'bg-gradient-to-r from-red-600/30 to-red-500/10 border border-red-500/30'
                                      : 'hover:bg-white/5'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${currentLesson?.id === lesson.id ? 'bg-red-600/40' : 'bg-white/10'}`}>
                                        {getContentIcon(lesson.content_type)}
                                      </div>
                                      <span className="text-sm font-medium">
                                        {lessonIndex + 1}. {lesson.title}
                                      </span>
                                    </div>
                                    
                                    {lessonProgress[lesson.id] === 100 && (
                                      <div className="bg-green-500/20 p-1 rounded-full">
                                        <CheckCircle size={14} className="text-green-400" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {lesson.duration_seconds && (
                                    <div className="text-xs text-white/50 mt-1.5 ml-9 flex items-center">
                                      <Clock size={12} className="mr-1.5 text-white/40" />
                                      {formatDuration(lesson.duration_seconds)}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Actions de la barre latérale */}
                  <div className="p-5 border-t border-white/10 bg-gradient-to-r from-black/40 to-black/20">
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-white/20 text-white hover:bg-white/10 py-5 font-medium rounded-lg bg-black/30 hover:bg-black/50 transition-colors"
                      >
                        <MessageCircle size={16} className="mr-2 text-red-400" />
                        Poser une question
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      </div>

      {/* Quiz Player */}
      {isQuizPlayerOpen && currentQuizLessonId && studentProfile && enrollmentId && (
        <QuizPlayer
          lessonId={currentQuizLessonId}
          studentId={studentProfile.id}
          enrollmentId={enrollmentId}
          onQuizComplete={handleQuizComplete}
          onClose={closeQuizPlayer}
        />
      )}
    </PublicLayout>
  );
};

export default CoursePlayerPage;
